import { Col, Hr, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { clearCachedReview, getCachedReview, isReviewMatchingCache } from "common/helpers/review-cache";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useProductFeedback } from "hooks/useProductFeedback";
import { CatalogicalDomain, UserPermissions } from "lib/services";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import React, { FC, useEffect, useState } from "react";

import { ProductReviewCard } from "./ProductReviewCard";

export interface ProductReviewsListProps {
  domain: CatalogicalDomain;
  taxonomyId: TaxonomyIdItem;
  refreshTrigger?: number; //triggers reviews refresh
}

export const ProductReviewsList: FC<ProductReviewsListProps> = (props) => {
  const {
    productFeedback: feedback,
    userFeedback,
    error,
    isLoading,
    doGetReviews,
    doGetUserReviews,
  } = useProductFeedback({ domain: props.domain });
  const username = UserPermissions.current.username ?? "";

  useEffect(() => {
    doGetReviews({ taxonomyId: props.taxonomyId });
    // not filtering by username for some reason
    // doGetUserReviews({ taxonomyId: props.taxonomyId, username: username });
  }, [props.refreshTrigger]);

  // Read cached review once and store in state; refresh when trigger changes
  const [cachedReview, setCachedReview] = useState<FeedbackModelRecord | null>(() =>
    props.taxonomyId ? getCachedReview(props.taxonomyId.toString()) : null
  );

  useEffect(() => {
    setCachedReview(props.taxonomyId ? getCachedReview(props.taxonomyId.toString()) : null);
  }, [props.refreshTrigger]);

  // Clear cache once backend has caught up (uses a deep match)
  useEffect(() => {
    if (cachedReview && feedback.some((f) => isReviewMatchingCache(cachedReview, f))) {
      clearCachedReview(props.taxonomyId.toString());
      setCachedReview(null);
    }
  }, [feedback, cachedReview]);

  const isValidReview = (feedback: FeedbackModelRecord) => {
    return (
      feedback &&
      feedback.title &&
      feedback.title?.length > 0 &&
      feedback.categories &&
      feedback.categories.length > 0 &&
      feedback.content &&
      feedback.content?.length > 0
    );
  };

  const pushSelfReview = (reviewCards: JSX.Element[]) => {
    if (userFeedback.length > 0) {
      const userReview = userFeedback[0]; // get first review, when sorting is available we should get the latest
      reviewCards.push(<Hr key={`hr-${userReview.feedbackId}`} />);
      reviewCards.push(
        <ProductReviewCard
          key={`review-${userReview.feedbackId}`}
          content={userReview.content!}
          jobFamily={userReview.jobFamily!}
          title={userReview.title!}
          categories={userReview.categories!}
          isSelf={true}
        />
      );
      // Remove user review from feedback array to prevent duplication
      const userReviewIndex = feedback.findIndex((f) => f.feedbackId === userReview.feedbackId);
      if (userReviewIndex > -1) {
        feedback.splice(userReviewIndex, 1);
      }
    }
  };

  const generateReviewCards = () => {
    const reviewCards: JSX.Element[] = [];

    // Show cached review at the top if backend hasn't indexed the latest version yet
    const hasCachedReview = cachedReview && !feedback.some((f) => isReviewMatchingCache(cachedReview, f));

    if (hasCachedReview && isValidReview(cachedReview)) {
      reviewCards.push(<Hr key={`hr-cached`} />);
      reviewCards.push(
        <ProductReviewCard
          key={`review-cached`}
          content={cachedReview.content!}
          jobFamily={cachedReview.jobFamily!}
          title={cachedReview.title!}
          categories={cachedReview.categories!}
          isSelf={true}
        />
      );
    }

    pushSelfReview(reviewCards);

    feedback.forEach((record: FeedbackModelRecord) => {
      // Skip the stale backend version if the cached review overwrites it
      if (hasCachedReview && record.feedbackId === cachedReview.feedbackId) return;

      if (isValidReview(record)) {
        reviewCards.push(<Hr key={`hr-${record.feedbackId}`} />);
        reviewCards.push(
          <ProductReviewCard
            key={`review-${record.feedbackId}`}
            content={record.content!}
            jobFamily={record.jobFamily!}
            title={record.title!}
            categories={record.categories!}
          />
        );
      }
    });
    if (reviewCards.length < 1) {
      reviewCards.push(NoReviews);
    }

    return reviewCards;
  };

  const renderLoadingComponent = () => {
    return (
      <Col alignItems="center" justifyContent="center" minHeight="400px">
        <Spinner size={SpinnerSize.Small} />
        <Text fontSize={"S100"}>{getString("productReviews.loading")}</Text>
      </Col>
    );
  };

  const NoReviews = (
    <View key="no-reviews" dataTestId="product-reviews-no-reviews">
      <Hr />
      <Text margin={"16px"}>{getString("productReviews.noReviews")}</Text>
    </View>
  );

  return (
    <Col dataTestId="product-reviews-list" height={"100%"} flex="1">
      {isLoading ? renderLoadingComponent() : generateReviewCards()}
    </Col>
  );
};
